import React from "react";
import * as Sentry from "@sentry/react";
import { Card, Typography } from "@mui/material";

/**
 * Contains a render failure to one section of a page instead of letting it reach
 * the router's errorElement, which would replace the whole route with ErrorPage.
 *
 * Used around lazily-loaded sections: a deploy replaces the content-hashed chunk
 * filenames, so a client still running the previous build can request a chunk the
 * new deployment no longer serves. That is a supplementary section failing to
 * arrive, not a broken page, and it should degrade accordingly.
 *
 * Pass `silent` to render nothing at all rather than a message.
 */
export default class SectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    const { name = "section" } = this.props;
    console.error(`SectionErrorBoundary (${name}) caught:`, error, info);

    // Error boundaries swallow errors, so without this they never reach Sentry's
    // global handlers — same trap QueryErrorBoundary documents.
    Sentry.captureException(error, {
      tags: {
        boundary: name,
        // Separates "a deploy invalidated a chunk" from an ordinary render bug.
        // webpack 5 names these ChunkLoadError; the message check covers the
        // native dynamic-import rejection browsers raise instead.
        chunk_load: isChunkLoadError(error),
      },
      contexts: { react: { componentStack: info.componentStack } },
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.silent) return null;

    return (
      <Card variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          This section couldn&apos;t be loaded. Refreshing the page usually fixes
          it.
        </Typography>
      </Card>
    );
  }
}

function isChunkLoadError(error) {
  return (
    error?.name === "ChunkLoadError" ||
    /Loading chunk|dynamically imported module|Importing a module script failed/i.test(
      error?.message ?? "",
    )
  );
}
